import { Injectable } from '@angular/core';

import { CartSessionTokenService } from './cart-session-token.service';
import { PublicCartService } from './public-cart.service';

type OwnershipCheckMessage = {
  type: 'ownership-check';
  requestId: string;
  senderInstanceId: string;
  tabId: string;
};

type OwnershipResponseMessage = {
  type: 'ownership-response';
  ownerInstanceId: string;
  requestId: string;
  tabId: string;
};

type TabSessionMessage = OwnershipCheckMessage | OwnershipResponseMessage;

export type TabSessionInitResult = {
  cloneDetected: boolean;
  previousTabId?: string;
  tabId: string;
};

@Injectable({ providedIn: 'root' })
export class TabSessionService {
  private readonly TAB_ID_KEY = 'tab_session_id';
  private readonly CHANNEL_NAME = 'mosaiku-tab-session';
  private readonly OWNERSHIP_CHECK_TIMEOUT_MS = 150;

  private readonly instanceId = this.generateId();
  private readonly pendingOwnershipChecks = new Map<string, () => void>();
  private readonly channel = this.createChannel();

  private currentTabId: string | null = null;
  private initPromise: Promise<TabSessionInitResult> | null = null;
  private lastInitResult: TabSessionInitResult | null = null;

  constructor(
    private cartSessionTokenService: CartSessionTokenService,
    private publicCartService: PublicCartService,
  ) {
    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent<TabSessionMessage>) => {
        this.handleChannelMessage(event.data);
      };
    }
  }

  initialize(): Promise<TabSessionInitResult> {
    if (!this.initPromise) {
      this.initPromise = this.runInitialization();
    }

    return this.initPromise;
  }

  consumeInitializationResult(): TabSessionInitResult | null {
    const result = this.lastInitResult;
    this.lastInitResult = null;
    return result;
  }

  getTabId(): string {
    if (!this.currentTabId) {
      const existingTabId = this.readTabId();
      this.currentTabId = existingTabId || this.writeNewTabId();
    }

    return this.currentTabId;
  }

  private async runInitialization(): Promise<TabSessionInitResult> {
    const existingTabId = this.readTabId();

    if (!existingTabId) {
      const tabId = this.writeNewTabId();
      const result = { cloneDetected: false, tabId };
      this.lastInitResult = result;
      return result;
    }

    this.currentTabId = existingTabId;

    const cloneDetected = await this.hasActiveOwner(existingTabId);
    if (!cloneDetected) {
      const result = { cloneDetected: false, tabId: existingTabId };
      this.lastInitResult = result;
      return result;
    }

    this.clearClonedCartState();
    const tabId = this.writeNewTabId();
    const result = { cloneDetected: true, previousTabId: existingTabId, tabId };
    this.lastInitResult = result;
    return result;
  }

  private async hasActiveOwner(tabId: string): Promise<boolean> {
    if (!this.channel) {
      return false;
    }

    const requestId = this.generateId();

    return await new Promise<boolean>((resolve) => {
      const timeoutId = window.setTimeout(() => {
        this.pendingOwnershipChecks.delete(requestId);
        resolve(false);
      }, this.OWNERSHIP_CHECK_TIMEOUT_MS);

      this.pendingOwnershipChecks.set(requestId, () => {
        clearTimeout(timeoutId);
        this.pendingOwnershipChecks.delete(requestId);
        resolve(true);
      });

      const message: OwnershipCheckMessage = {
        type: 'ownership-check',
        requestId,
        senderInstanceId: this.instanceId,
        tabId,
      };

      this.channel?.postMessage(message);
    });
  }

  private handleChannelMessage(message: TabSessionMessage | null | undefined): void {
    if (!message) {
      return;
    }

    if (message.type === 'ownership-check') {
      this.respondToOwnershipCheck(message);
      return;
    }

    const resolveOwnershipCheck = this.pendingOwnershipChecks.get(message.requestId);
    if (resolveOwnershipCheck && message.ownerInstanceId !== this.instanceId) {
      resolveOwnershipCheck();
    }
  }

  private respondToOwnershipCheck(message: OwnershipCheckMessage): void {
    const currentTabId = this.currentTabId || this.readTabId();
    if (!currentTabId) {
      return;
    }

    if (message.tabId !== currentTabId || message.senderInstanceId === this.instanceId) {
      return;
    }

    const response: OwnershipResponseMessage = {
      type: 'ownership-response',
      ownerInstanceId: this.instanceId,
      requestId: message.requestId,
      tabId: currentTabId,
    };

    this.channel?.postMessage(response);
  }

  private clearClonedCartState(): void {
    this.cartSessionTokenService.clear();
    this.publicCartService.clear();
  }

  private readTabId(): string | null {
    try {
      const value = sessionStorage.getItem(this.TAB_ID_KEY);
      return value && value.trim() ? value : null;
    } catch {
      return null;
    }
  }

  private writeNewTabId(): string {
    const tabId = this.generateId();

    try {
      sessionStorage.setItem(this.TAB_ID_KEY, tabId);
    } catch {
      // ignore storage errors
    }

    this.currentTabId = tabId;
    return tabId;
  }

  private createChannel(): BroadcastChannel | null {
    if (typeof BroadcastChannel === 'undefined') {
      return null;
    }

    try {
      return new BroadcastChannel(this.CHANNEL_NAME);
    } catch {
      return null;
    }
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}
